import type {
  ChatOut,
  ChatRequest,
  ChatSummaryPage,
  ChatUpdate,
  FileExpandResponse,
  GenerateTitleRequest,
  GenerateTitleResponse,
  ListChatsParams,
  ListModelsParams,
  ModelOut,
  ModelPage,
  Source,
} from "../types/cuadra";
import type { ArtifactFull, ArtifactListResponse } from "../types/artifact";
import { generateSecureUUID } from "../utils/sanitize";

/**
 * SSE Chunk types for streaming responses
 * Supports current API format and AI SDK UI Message Stream Protocol
 */
export interface SSEChunk {
  // Common fields
  type?: string;
  id?: string; // Chat/message ID or block ID

  // Current API format fields
  delta?: string; // Delta content (incremental text)
  reasoning?: string; // Accumulated reasoning content
  sources?: Source[] | null; // RAG sources (sent in first chunk)
  toolCalls?: unknown[] | null; // Tool calls (future)
  finished?: boolean; // Whether the stream is finished
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    cost?: number | null;
  };

  // Legacy format fields
  content?: string;
  chatId?: string;
  /** Some backends emit snake_case on `type: "chat_created"` events */
  chat_id?: string;
  message?: {
    id?: string;
    role: string;
    content: string;
  };
  done?: boolean;

  // AI SDK Protocol: Source document event
  sourceId?: string;
  mediaType?: string;
  title?: string; // Filename for source-document events

  // AI SDK Protocol: Message metadata
  messageId?: string;

  // AI SDK Protocol: Finish event
  finishReason?: string;

  // AI SDK Protocol: Error event
  errorText?: string;

  // Inline error object (returned with finished: true on server errors)
  error?: {
    code: string;
    message: string;
  };

  // Artifact events
  data?: {
    id?: string;
    identifier?: string;
    title?: string;
    type?: string;
    language?: string | null;
    contentDelta?: string;
    version?: number;
    error?: string;
  };

  // Tool input events
  toolName?: string;
  args?: Record<string, unknown>;
  /** When present with toolName, use as role "tool" message toolCallId for client ack */
  toolCallId?: string;
}

/**
 * CuadraChatClient - API client for Cuadra AI REST API
 * Handles authentication, HTTP requests, and SSE streaming
 */
export class CuadraChatClient {
  private baseUrl: string;
  private sessionToken: string | null = null;
  private isProxyMode: boolean = false;
  private onUnauthorized: (() => void) | null = null;

  constructor(
    baseUrl: string,
    sessionToken?: string,
    isProxyMode: boolean = false,
  ) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
    this.isProxyMode = isProxyMode;
    if (sessionToken) {
      this.sessionToken = sessionToken;
    }
  }

  setSessionToken(token: string | null): void {
    this.sessionToken = token;
  }

  /**
   * Register a callback invoked when a 401 is received from the API.
   * The parent app should use this to trigger a token refresh cycle.
   */
  setOnUnauthorized(callback: (() => void) | null): void {
    this.onUnauthorized = callback;
  }

  /**
   * Check an HTTP response for 401 and notify the parent via callback.
   * Returns true when a 401 was detected so callers can branch.
   */
  private checkUnauthorized(status: number): boolean {
    if (status === 401 && this.onUnauthorized) {
      this.onUnauthorized();
      return true;
    }
    return false;
  }

  /**
   * Resolve a relative API path to a full URL.
   * In proxy mode the `/v1` prefix is stripped; otherwise `baseUrl` is prepended.
   * Useful for constructing file download URLs from the relative paths
   * returned in `MessageFileOut.url`.
   */
  getUrl(endpoint: string): string {
    if (this.isProxyMode) {
      // Proxy mode: remove /v1 prefix
      const path = `${this.baseUrl}${endpoint.replace("/v1", "")}`;
      // If baseUrl is relative, return as-is; otherwise construct full URL
      if (path.startsWith("/") || path.startsWith("./")) {
        return path;
      }
      return path;
    }
    return `${this.baseUrl}${endpoint}`;
  }

  async createOrContinueChat(
    request: ChatRequest,
    abortSignal?: AbortSignal,
  ): Promise<ReadableStream<Uint8Array>> {
    const url = this.getUrl("/v1/chats");

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      "Accept-Encoding": "identity",
      "Cache-Control": "no-cache",
      "X-Accel-Buffering": "no",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    // Ensure stream is true for streaming
    const body: ChatRequest = {
      ...request,
      stream: true,
    };

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: abortSignal,
      cache: "no-store",
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    if (!response.body) {
      throw new Error("Response body is null");
    }

    return response.body;
  }

  /**
   * Persist a client-executed tool result for an existing chat (non-streaming).
   * The Cuadra API expects the client to execute tools and send role "tool"
   * messages with toolCallId so the conversation can continue.
   */
  async submitToolResult(
    request: ChatRequest,
    abortSignal?: AbortSignal,
  ): Promise<unknown> {
    const url = this.getUrl("/v1/chats");

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    // Non-streaming chat mutations require Idempotency-Key on the API.
    headers["Idempotency-Key"] = this.generateIdempotencyKey();

    const body: ChatRequest = {
      ...request,
      stream: false,
    };

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: abortSignal,
      cache: "no-store",
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return response.json().catch(() => ({}));
  }

  /**
   * Get a specific chat by ID
   */
  async getChat(chatId: string): Promise<ChatOut> {
    const url = this.getUrl(`/v1/chats/${chatId}`);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  }

  /**
   * List chats with pagination
   */
  async listChats(params?: ListChatsParams): Promise<ChatSummaryPage> {
    const baseUrl = this.getUrl("/v1/chats");
    // Handle relative URLs (for proxy mode)
    const url =
      baseUrl.startsWith("/") || baseUrl.startsWith("./")
        ? new URL(baseUrl, window.location.origin)
        : new URL(baseUrl);

    if (params?.limit) {
      url.searchParams.set("limit", params.limit.toString());
    }
    if (params?.cursor) {
      url.searchParams.set("cursor", params.cursor);
    }
    if (params?.["expand[]"]) {
      params["expand[]"].forEach((expand) => {
        url.searchParams.append("expand[]", expand);
      });
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  }

  /**
   * Generate a cryptographically secure UUID v4 idempotency key
   * Uses the shared secure UUID generator from sanitize utils
   */
  private generateIdempotencyKey(): string {
    return generateSecureUUID();
  }

  /**
   * Update a chat
   */
  async updateChat(chatId: string, update: ChatUpdate): Promise<ChatOut> {
    const endpoint = `/v1/chats/${chatId}`;
    const baseUrl = this.getUrl(endpoint);

    // Handle relative URLs (for proxy mode)
    const url =
      baseUrl.startsWith("/") || baseUrl.startsWith("./")
        ? new URL(baseUrl, window.location.origin)
        : new URL(baseUrl);

    // Generate idempotency key for update operation
    const idempotencyKey = this.generateIdempotencyKey();

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    };

    if (this.sessionToken) {
      (headers as Record<string, string>)["Authorization"] =
        `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url.toString(), {
      method: "PATCH",
      headers,
      body: JSON.stringify(update),
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorText = await response.text();
      let errorData: { detail?: string; message?: string } = {};
      try {
        errorData = JSON.parse(errorText) as {
          detail?: string;
          message?: string;
        };
      } catch {
        errorData = { detail: errorText };
      }
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    // Handle empty responses (e.g., 204 No Content)
    if (response.status === 204) {
      return {} as ChatOut;
    }

    const text = await response.text();
    if (!text) {
      return {} as ChatOut;
    }

    try {
      return JSON.parse(text);
    } catch {
      return {} as ChatOut;
    }
  }

  /**
   * Delete a chat
   */
  async deleteChat(chatId: string): Promise<void> {
    const endpoint = `/v1/chats/${chatId}`;
    const baseUrl = this.getUrl(endpoint);

    // Handle relative URLs (for proxy mode)
    const url =
      baseUrl.startsWith("/") || baseUrl.startsWith("./")
        ? new URL(baseUrl, window.location.origin)
        : new URL(baseUrl);

    // Generate idempotency key for delete operation
    const idempotencyKey = this.generateIdempotencyKey();

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    };

    if (this.sessionToken) {
      (headers as Record<string, string>)["Authorization"] =
        `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url.toString(), {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }
  }

  /**
   * Generate an AI-powered title for a chat.
   * Uses a fast model on the backend to analyze the conversation.
   */
  async generateChatTitle(
    chatId: string,
    request?: GenerateTitleRequest,
  ): Promise<GenerateTitleResponse> {
    const endpoint = `/v1/chats/${chatId}/title`;
    const baseUrl = this.getUrl(endpoint);

    const url =
      baseUrl.startsWith("/") || baseUrl.startsWith("./")
        ? new URL(baseUrl, window.location.origin)
        : new URL(baseUrl);

    const idempotencyKey = this.generateIdempotencyKey();

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url.toString(), {
      method: "POST",
      headers,
      body: request ? JSON.stringify(request) : undefined,
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  }

  /**
   * List available models
   */
  async listModels(params?: ListModelsParams): Promise<ModelPage> {
    const baseUrl = this.getUrl("/v1/models");
    // Handle relative URLs (for proxy mode)
    const url =
      baseUrl.startsWith("/") || baseUrl.startsWith("./")
        ? new URL(baseUrl, window.location.origin)
        : new URL(baseUrl);

    if (params?.limit) {
      url.searchParams.set("limit", params.limit.toString());
    }
    if (params?.cursor) {
      url.searchParams.set("cursor", params.cursor);
    }
    if (params?.["expand[]"]) {
      params["expand[]"].forEach((expand) => {
        url.searchParams.append("expand[]", expand);
      });
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  }

  /**
   * Get a specific model by ID
   */
  async getModel(modelId: string): Promise<ModelOut> {
    const url = this.getUrl(`/v1/models/${modelId}`);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  }

  /**
   * List artifacts for a chat (latest version per identifier).
   */
  async listArtifacts(chatId: string): Promise<ArtifactListResponse> {
    const url = this.getUrl(`/v1/chats/${chatId}/artifacts`);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ??
          errorData.message ??
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  }

  /**
   * Get a single artifact with its content.
   */
  async getArtifact(chatId: string, artifactId: string): Promise<ArtifactFull> {
    const url = this.getUrl(`/v1/chats/${chatId}/artifacts/${artifactId}`);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ??
          errorData.message ??
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  }

  /**
   * Build the download URL for an artifact's raw content.
   * The backend serves it with Content-Disposition: attachment.
   */
  getArtifactDownloadUrl(chatId: string, artifactId: string): string {
    return this.getUrl(`/v1/chats/${chatId}/artifacts/${artifactId}/content`);
  }

  /**
   * Fetch artifact content with auth and trigger a browser download.
   * Use this instead of a bare <a href> link, which cannot send the Bearer token.
   */
  async downloadArtifactContent(
    chatId: string,
    artifactId: string,
    filename: string,
  ): Promise<void> {
    const url = this.getUrl(
      `/v1/chats/${chatId}/artifacts/${artifactId}/content`,
    );
    const headers: Record<string, string> = {};

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      throw new Error(
        `Download failed: ${response.status} ${response.statusText}`,
      );
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  /**
   * Upload a file to Cuadra API
   * @param file - The file to upload
   * @param resourceType - Optional resource type (e.g., 'chat')
   * @param resourceId - Optional resource ID to associate with
   * @returns Promise resolving to file metadata
   */
  async uploadFile(
    file: File,
    resourceType?: string,
    resourceId?: string,
  ): Promise<{ id: string; name: string; [key: string]: unknown }> {
    const url = this.getUrl("/v1/files");

    // Generate idempotency key
    const idempotencyKey = this.generateIdempotencyKey();

    const formData = new FormData();
    formData.append("file", file);

    if (resourceType) {
      formData.append("resource_type", resourceType);
    }
    if (resourceId) {
      formData.append("resource_id", resourceId);
    }

    const headers: Record<string, string> = {
      "Idempotency-Key": idempotencyKey,
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  }

  /**
   * Expand a ZIP archive into individual files on the server.
   * Returns created files and any skipped entries (unsupported types, nested ZIPs, etc.).
   * A 200 with an empty `files` array and non-empty `skipped` is valid (fully-skipped), not an error.
   */
  async expandZipFile(
    file: File,
    resourceType?: string,
    resourceId?: string,
  ): Promise<FileExpandResponse> {
    const url = this.getUrl("/v1/files/expand");

    const idempotencyKey = this.generateIdempotencyKey();

    const formData = new FormData();
    formData.append("file", file);

    if (resourceType) {
      formData.append("resource_type", resourceType);
    }
    if (resourceId) {
      formData.append("resource_id", resourceId);
    }

    const headers: Record<string, string> = {
      "Idempotency-Key": idempotencyKey,
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ||
          errorData.message ||
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    return response.json();
  }

  /**
   * List available datasets for the authenticated user.
   * Returns a minimal subset of dataset fields needed for the picker UI.
   */
  async listDatasets(): Promise<
    Array<{ id: string; name: string; description?: string | null }>
  > {
    const url = this.getUrl("/v1/datasets?limit=100&offset=0");
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
      this.checkUnauthorized(response.status);
      const errorData = await response.json().catch(() => ({}));
      throw new CuadraApiError(
        errorData.detail ??
          errorData.message ??
          `API error: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    const data = await response.json();
    return (data.items ?? []).map((d: Record<string, unknown>) => ({
      id: d.id as string,
      name: (d.name as string) || "",
      description: (d.description as string | null | undefined) ?? null,
    }));
  }

  /**
   * Submit feedback on a RAG source chunk.
   * Errors 400/404 are swallowed (spec requirement: must not interrupt chat UX).
   * Re-submitting for the same (messageId, chunkId, userId) updates the existing rating.
   */
  async submitSourceFeedback(params: {
    chatId: string;
    messageId: string;
    chunkId: string;
    rating: "positive" | "negative";
  }): Promise<void> {
    const url = this.getUrl(
      `/v1/chats/${params.chatId}/messages/${params.messageId}/source-feedback`,
    );

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          chunkId: params.chunkId,
          rating: params.rating,
        }),
      });

      if (response.status === 401) {
        this.checkUnauthorized(response.status);
      }
      // 400/404 fail silently per spec
    } catch {
      // Network errors fail silently — must not interrupt chat UX
    }
  }

  /**
   * Submit feedback on an entire assistant response.
   * Re-submitting for the same (messageId, userId) updates the existing rating.
   * Errors are swallowed — must not interrupt chat UX.
   */
  async submitMessageFeedback(params: {
    chatId: string;
    messageId: string;
    rating: "positive" | "negative";
    comment?: string | null;
  }): Promise<void> {
    const url = this.getUrl(
      `/v1/chats/${params.chatId}/messages/${params.messageId}/feedback`,
    );

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    const body: Record<string, unknown> = { rating: params.rating };
    if (params.comment) {
      body.comment = params.comment;
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (response.status === 401) {
        this.checkUnauthorized(response.status);
      }
    } catch {
      // Network errors fail silently — must not interrupt chat UX
    }
  }
}

/**
 * Error thrown when a Cuadra API request fails.
 * Carries the HTTP status so callers can react to specific codes (e.g. 402).
 */
export class CuadraApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "CuadraApiError";
  }
}

// ---------------------------------------------------------------------------
// File auth store — module-level config so rendering components can fetch
// authenticated file content without direct access to the CuadraChatClient.
// ---------------------------------------------------------------------------

export interface FileAuthConfig {
  baseUrl: string;
  sessionToken?: string;
  isProxyMode: boolean;
}

/** Current file auth config. Updated by CuadraRuntimeProvider on mount. */
let _fileAuthConfig: FileAuthConfig | null = null;

export function setFileAuthConfig(config: FileAuthConfig | null): void {
  _fileAuthConfig = config;
}

export function getFileAuthConfig(): FileAuthConfig | null {
  return _fileAuthConfig;
}

/**
 * Parse SSE stream into chunks
 */
export async function* parseSSEStream(
  stream: ReadableStream<Uint8Array>,
  abortSignal?: AbortSignal,
): AsyncGenerator<SSEChunk> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      if (abortSignal?.aborted) {
        reader.cancel();
        break;
      }

      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = line.slice(6);
          if (data.trim() === "[DONE]") {
            yield { done: true };
            continue;
          }
          try {
            const parsed = JSON.parse(data);
            yield parsed;
          } catch {
            // Skip invalid JSON
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
